'use client';

import Cookies from 'js-cookie';
import {
  Button,
  Container,
  Divider,
  Tabs,
  Text,
  Stack,
  Box,
} from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { useEffect, useState } from 'react';
import { FullSizeLoader } from '@/src/components/Loader/FullSizeLoader';
import { useDictionary } from '@/src/dictionaries/dictionary-provider';
import { useAuthValidation } from '@/src/lib/hooks/useAuthValidation';
import classes from './admin.module.scss';
import AdminDashboardPanel from '@/src/components/AdminDashboardPanel/AdminDashboardPanel';
import SiteOverviewPanel from '@/src/components/AdminDashboardPanel/SiteOverviewPanel';

const Admin = () => {
  const dictionary = useDictionary();
  const mobile = useMediaQuery('(max-width: 48em)');
  const { isLoading } = useAuthValidation();

  const [articles, setArticles] = useState({ count: 0, recent: [] });
  const [products, setProducts] = useState({ count: 0, recent: [] });
  const [estimations, setEstimations] = useState({ count: 0, recent: [] });
  const [visitors, setVisitors] = useState(0);

  useEffect(() => {
    const fetchAll = async () => {
      const endpoints = [
        ['/api/articles/summary', setArticles],
        ['/api/products/summary', setProducts],
        ['/api/estimation/summary', setEstimations],
        ['/api/track-visit/count', (data: any) => setVisitors(data.count || 0)],
      ];

      for (const [url, setter] of endpoints) {
        try {
          // @ts-ignore
          const res = await fetch(url);
          const data = await res.json();
          if (url === '/api/track-visit/count') {
            // @ts-ignore
            setter(data);
          } else {
            // @ts-ignore
            setter({
              count: data.total || 0,
              recent: (data.latest || []).map((item: any) => ({
                id: item._id,
                title: item.title,
                imageUrl: item.mainImage || '',
                description: item.description || '',
                link: item.link || '',
              })),
            });
          }
        } catch (err) {
          console.error(`Error fetching ${url}:`, err);
        }
      }
    };


    fetchAll();
  }, []);

  if (isLoading) return <FullSizeLoader />;

  const handleLogout = () => {
    Cookies.remove('authToken');
    window.location.reload();
  };

  const tabItems = [
    { value: 'overview', label: dictionary.pages.admin.overview },
    // { value: 'estimations', label: dictionary.pages.admin.manageEstimations },
    { value: 'products', label: dictionary.pages.admin.addProducts },
    { value: 'articles', label: dictionary.pages.admin.addArticles },
  ];

  const renderTabList = () => (
    <Box className={classes.tabsList}>
      <Tabs.List>
        {tabItems.map((tab) => (
          <Tabs.Tab key={tab.value} value={tab.value}>
            {tab.label}
          </Tabs.Tab>
        ))}
      </Tabs.List>
      {!mobile && (
        <div className={classes.logoutWrapper}>
          <Divider my="md" />
          <Button color="red" fullWidth onClick={handleLogout}>
            {dictionary.header.logout}
          </Button>
        </div>
      )}
    </Box>
  );

  const renderPanels = () => (
    <div className={classes.contentArea}>
      <h2 className={classes.title}>{dictionary.pages.admin.title}</h2>
      <Tabs.Panel value="overview">
        <Stack>
          <SiteOverviewPanel
            visitors={visitors}
            articles={articles}
            products={products}
            estimations={estimations}
          />
        </Stack>
      </Tabs.Panel>

      <Tabs.Panel value="estimations">
        <Container>
          {/* <Text>{dictionary.pages.admin.manageEstimations}</Text> */}
          <AdminDashboardPanel
            total={estimations.count}
            createUrl="/"
            cards={estimations.recent}
            titleLabel={dictionary.pages.admin.totalEstimations}
            // createButtonLabel={dictionary.pages.admin.manageEstimations}
            createButtonVisible={false}
            seeMoreVisible={true}
            seeMoreRedirect="/admin/estimations"
            seeMoreLabel={dictionary.pages.admin.seeMoreEstimations}
            maxCards={3}
          />
        </Container>
      </Tabs.Panel>

      <Tabs.Panel value="products">
        <Container>
          <Text>{dictionary.pages.admin.addProducts}</Text>
          <AdminDashboardPanel
            total={products.count}
            createUrl="/admin/products"
            cards={products.recent}
            titleLabel={dictionary.pages.admin.totalArticles}
            createButtonLabel={dictionary.pages.admin.manageProducts}
            createButtonVisible={true}
            seeMoreVisible={true}
            seeMoreRedirect="/products"
            seeMoreLabel={dictionary.pages.admin.seeMoreProducts}
            maxCards={3}
          />
        </Container>
      </Tabs.Panel>

      <Tabs.Panel value="articles">
        <Container>
          <Text>{dictionary.pages.admin.addArticles}</Text>
          <AdminDashboardPanel
            total={articles.count}
            createUrl="/admin/articles"
            cards={articles.recent}
            titleLabel={dictionary.pages.admin.totalArticles}
            createButtonLabel={dictionary.pages.admin.manageArticles}
            createButtonVisible={true}
            seeMoreVisible={true}
            seeMoreRedirect="/articles"
            seeMoreLabel={dictionary.pages.admin.seeMoreArticles}
            maxCards={3}
          />
        </Container>
      </Tabs.Panel>
    </div>
  );

  return (
    <div className={classes.window}>
      <div className={classes.pageWrapper}>
        <Tabs
          orientation={mobile ? 'horizontal' : 'vertical'}
          defaultValue="overview"
          classNames={{ tab: classes.tab }}
          keepMounted={false}
        >
          {mobile ? (
            <>
              {renderTabList()}
              {renderPanels()}
            </>
          ) : (
            <>
              {renderPanels()}
              {renderTabList()}
            </>
          )}
        </Tabs>
      </div>
    </div>
  );
};

export default Admin;
